import React, { useState } from 'react';
import {
  Bot, User, CheckCircle, XCircle, AlertTriangle, ChevronDown,
  ChevronRight, Wrench, Zap, Loader2, Clock, Image as ImageIcon, FileText
} from 'lucide-react';

// ── Simple Markdown Renderer ─────────────────────────────────────────────────

function formatInline(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">$1</code>');
}

function MarkdownContent({ text }) {
  if (!text) return null;

  // Split by code blocks first
  const segments = text.split(/(```[\s\S]*?```)/);

  return (
    <div className="space-y-1">
      {segments.map((segment, si) => {
        // Code block
        if (segment.startsWith('```')) {
          const content = segment.replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
          return (
            <pre key={si} className="bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-2 leading-relaxed">
              <code>{content}</code>
            </pre>
          );
        }

        // Process line by line
        const lines = segment.split('\n');
        return lines.map((line, li) => {
          const key = `${si}-${li}`;

          if (!line.trim()) return <div key={key} className="h-1" />;

          // Headers
          if (line.startsWith('#### '))
            return <h5 key={key} className="text-xs font-semibold mt-2 mb-0.5 text-gray-800 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: formatInline(line.slice(5)) }} />;
          if (line.startsWith('### '))
            return <h4 key={key} className="text-sm font-semibold mt-2 mb-0.5 text-gray-900 dark:text-white" dangerouslySetInnerHTML={{ __html: formatInline(line.slice(4)) }} />;
          if (line.startsWith('## '))
            return <h3 key={key} className="text-sm font-bold mt-3 mb-0.5 text-gray-900 dark:text-white" dangerouslySetInnerHTML={{ __html: formatInline(line.slice(3)) }} />;
          if (line.startsWith('# '))
            return <h2 key={key} className="text-base font-bold mt-3 mb-1 text-gray-900 dark:text-white" dangerouslySetInnerHTML={{ __html: formatInline(line.slice(2)) }} />;

          // Unordered list
          if (/^[-*]\s/.test(line))
            return (
              <div key={key} className="flex gap-1.5 text-sm leading-relaxed">
                <span className="text-gray-400 mt-0.5 flex-shrink-0">&bull;</span>
                <span dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^[-*]\s/, '')) }} />
              </div>
            );

          // Ordered list
          if (/^\d+\.\s/.test(line)) {
            const num = line.match(/^(\d+)\./)?.[1] || '';
            return (
              <div key={key} className="flex gap-1.5 text-sm leading-relaxed">
                <span className="text-gray-400 font-mono text-xs w-4 text-right flex-shrink-0 mt-0.5">{num}.</span>
                <span dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^\d+\.\s/, '')) }} />
              </div>
            );
          }

          // Regular paragraph
          return <p key={key} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: formatInline(line) }} />;
        });
      })}
    </div>
  );
}

// ── Streaming Progress Timeline ──────────────────────────────────────────────

/**
 * StreamingProgress — Vertikale Timeline der Agent-Schritte
 * Zeigt live welcher Schritt gerade ausgefuehrt wird.
 */
export function StreamingProgress({ steps, currentStep }) {
  if (!steps || steps.length === 0) return null;

  const getStepIcon = (step) => {
    switch (step.status) {
      case 'active':
        return <Loader2 className="w-3.5 h-3.5 text-indigo-500 animate-spin" />;
      case 'done':
        return <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />;
      case 'error':
        return <XCircle className="w-3.5 h-3.5 text-red-500" />;
      case 'waiting':
        return <Clock className="w-3.5 h-3.5 text-amber-500" />;
      default:
        return <div className="w-3.5 h-3.5 rounded-full bg-gray-300 dark:bg-gray-600" />;
    }
  };

  const getStepColor = (step) => {
    switch (step.status) {
      case 'active': return 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/10';
      case 'done': return 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/5';
      case 'error': return 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/5';
      case 'waiting': return 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/5';
      default: return 'border-gray-200 dark:border-gray-700';
    }
  };

  // Tool-Name formatieren: "wordpress.listPages" → "Seiten laden"
  const formatToolLabel = (tool) => {
    if (!tool) return '';
    const labels = {
      'pages.listPages': 'Seiten laden',
      'pages.getPage': 'Seite laden',
      'pages.createPage': 'Seite erstellen',
      'pages.updatePage': 'Seite aktualisieren',
      'pages.deletePage': 'Seite loeschen',
      'menus.listMenus': 'Menues laden',
      'menus.getMenu': 'Menu laden',
      'menus.createMenu': 'Menu erstellen',
      'menus.createMenuItem': 'Menu-Eintrag erstellen',
      'menus.assignMenuLocation': 'Menu zuweisen',
      'menus.getPageTree': 'Seitenbaum laden',
      'dna.getContext': 'DNA-Kontext laden',
      'dna.getKnowledge': 'DNA-Wissen laden',
      'dna.getKnowledgeGrouped': 'DNA laden',
      'dna.semanticSearch': 'Semantische Suche',
      'dna.createKnowledge': 'DNA-Eintrag erstellen',
      'seo.analyzePost': 'SEO analysieren',
      'seo.getOverview': 'SEO-Uebersicht',
      'seo.updateMeta': 'SEO-Meta setzen',
      'media.generateImage': 'Bild generieren',
      'brand.get': 'Brand Kit laden',
      'research.fetchUrl': 'URL abrufen',
      'research.extractDesign': 'Design extrahieren',
      'content.generate': 'Content generieren'
    };
    return labels[tool] || tool.split('.').pop();
  };

  return (
    <div className="mb-4 ml-1">
      {/* Timeline */}
      <div className="relative space-y-1.5">
        {/* Vertikale Linie */}
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200 dark:bg-gray-700" />

        {steps.map((step, i) => (
          <div key={i} className="flex items-start gap-2.5 relative">
            {/* Icon */}
            <div className="relative z-10 bg-white dark:bg-gray-900 p-px">
              {getStepIcon(step)}
            </div>

            {/* Label */}
            <div className={`flex-1 px-2.5 py-1.5 rounded-lg border text-xs ${getStepColor(step)}`}>
              <span className={`font-medium ${
                step.status === 'active' ? 'text-indigo-700 dark:text-indigo-300' :
                step.status === 'done' ? 'text-emerald-700 dark:text-emerald-400' :
                step.status === 'error' ? 'text-red-700 dark:text-red-400' :
                step.status === 'waiting' ? 'text-amber-700 dark:text-amber-400' :
                'text-gray-600 dark:text-gray-400'
              }`}>
                {step.type === 'tool'
                  ? formatToolLabel(step.tool)
                  : step.type === 'approval'
                    ? `Freigabe: ${formatToolLabel(step.tool)}`
                    : step.label
                }
              </span>
              {step.error && (
                <span className="text-red-500 ml-1.5">{step.error.slice(0, 60)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tool Result Card ─────────────────────────────────────────────────────────

function ToolResultCard({ tr }) {
  const [expanded, setExpanded] = useState(false);
  const isError = !!tr.error;
  const toolName = tr.tool?.split('.').pop() || tr.tool;
  const category = tr.tool?.split('.')[0] || '';

  const hasDetails = tr.result && typeof tr.result === 'object' &&
    (Array.isArray(tr.result) ? tr.result.length > 0 : Object.keys(tr.result).length > 0);

  // Bildvorschau: Wenn result.url auf Bildendung oder result.mediaId existiert
  const imageUrl = tr.result?.url && /\.(png|jpg|jpeg|webp|gif|svg)(\?|$)/i.test(tr.result.url)
    ? tr.result.url
    : null;
  const hasImage = imageUrl || tr.result?.mediaId;

  return (
    <div className={`rounded-lg border text-xs ${
      isError
        ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
        : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10'
    }`}>
      <button
        onClick={() => (hasDetails || hasImage) && setExpanded(!expanded)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left ${(hasDetails || hasImage) ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {isError ? (
          <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
        ) : hasImage ? (
          <ImageIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
        ) : (
          <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
        )}
        <span className="text-gray-400 dark:text-gray-500 font-mono">{category}.</span>
        <span className={`font-medium ${isError ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
          {toolName}
        </span>
        <span className="text-gray-500 dark:text-gray-400 ml-auto truncate max-w-[140px]">
          {isError
            ? tr.error?.slice(0, 50)
            : hasImage
              ? 'Bild hochgeladen'
              : typeof tr.result === 'string'
                ? tr.result.slice(0, 40)
                : Array.isArray(tr.result)
                  ? `${tr.result.length} Ergebnis${tr.result.length !== 1 ? 'se' : ''}`
                  : tr.result?.total
                    ? `${tr.result.total} Ergebnis${tr.result.total !== 1 ? 'se' : ''}`
                    : tr.result?.seo?.filename
                      ? tr.result.seo.filename.slice(0, 30)
                      : 'Erfolgreich'
          }
        </span>
        {(hasDetails || hasImage) && (
          expanded
            ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
            : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-2 border-t border-gray-200 dark:border-gray-700 pt-2 space-y-2">
          {/* Bildvorschau */}
          {imageUrl && (
            <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <img
                src={imageUrl}
                alt={tr.result?.seo?.altText || tr.result?.revisedPrompt || 'Generiertes Bild'}
                className="w-full max-h-48 object-cover"
                loading="lazy"
              />
              {tr.result?.seo?.altText && (
                <div className="px-2 py-1.5 bg-gray-50 dark:bg-gray-800 text-[10px] text-gray-500 dark:text-gray-400">
                  Alt: {tr.result.seo.altText.slice(0, 80)}...
                </div>
              )}
            </div>
          )}

          {/* JSON Details */}
          {hasDetails && (
            <pre className="text-xs font-mono text-gray-600 dark:text-gray-400 overflow-x-auto max-h-32 whitespace-pre-wrap">
              {JSON.stringify(tr.result, null, 2).slice(0, 800)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ── Approval Card (einzeln) ──────────────────────────────────────────────────

const TOOL_LABELS = {
  'pages.createPage': 'Seite erstellen',
  'pages.updatePage': 'Seite aktualisieren',
  'pages.deletePage': 'Seite loeschen',
  'menus.createMenu': 'Menue erstellen',
  'menus.createMenuItem': 'Menuepunkt erstellen',
  'menus.assignMenuLocation': 'Menue zuweisen',
  'content.generate': 'Content generieren',
  'seo.updateMeta': 'SEO-Daten aktualisieren',
  'brand.update': 'Branding aktualisieren'
};

function ApprovalCard({ approval, onApprove, onReject }) {
  const label = TOOL_LABELS[approval.tool] || approval.tool?.split('.').pop() || approval.tool;
  const params = approval.parameters || {};
  const detail = params.title || params.name || params.topic || params.template || '';

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
      <Zap className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-gray-900 dark:text-white">{label}</span>
        {detail && (
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1.5">— {String(detail).slice(0, 50)}</span>
        )}
      </div>
    </div>
  );
}

// ── Main Message Component ───────────────────────────────────────────────────

export default function AgentMessage({ message, onApprove, onReject, onApproveAll }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  return (
    <div className={`mb-4 ${isUser ? 'flex justify-end' : ''}`}>
      {/* User Message */}
      {isUser && (
        <div className="max-w-[85%]">
          <div className="px-4 py-2.5 rounded-2xl rounded-br-md bg-indigo-600 text-white text-sm leading-relaxed">
            {message.content}
          </div>
          {/* Datei-Anhang unter der User-Nachricht */}
          {message.attachment && (
            <div className="mt-1.5 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {message.attachment.type === 'image' ? (
                <img
                  src={message.attachment.preview || message.attachment.url}
                  alt={message.attachment.filename || 'Hochgeladenes Bild'}
                  className="w-full max-h-32 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-gray-400" />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{message.attachment.filename}</span>
                </div>
              )}
              <div className="px-2.5 py-1.5 flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
                {message.attachment.type === 'image' ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                <span className="truncate">{message.attachment.filename}</span>
                {message.attachment.mediaId && (
                  <span className="ml-auto text-gray-400">ID: {message.attachment.mediaId}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Message */}
      {isSystem && message.content && (
        <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
          {message.content}
        </div>
      )}

      {/* Assistant Message */}
      {!isUser && !isSystem && (
        <div className="max-w-full">
          {/* Text */}
          {message.content && (
            <div className="text-gray-800 dark:text-gray-200">
              <MarkdownContent text={message.content} />
            </div>
          )}
        </div>
      )}

      {/* Tool Results */}
      {message.toolResults?.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {message.toolResults.map((tr, i) => (
            <ToolResultCard key={i} tr={tr} />
          ))}
        </div>
      )}

      {/* Pending Approvals */}
      {message.pendingApprovals?.length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/15 overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-100/60 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {message.pendingApprovals.length} {message.pendingApprovals.length === 1 ? 'Aktion' : 'Aktionen'} warten auf Freigabe
            </span>
          </div>

          {/* Approval Items */}
          <div className="p-3 space-y-1.5">
            {message.pendingApprovals.map((approval) => (
              <ApprovalCard
                key={approval.approvalId}
                approval={approval}
                onApprove={onApprove}
                onReject={onReject}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 px-4 py-3 border-t border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
            <button
              onClick={() => onApproveAll?.()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <CheckCircle className="w-4 h-4" />
              {message.pendingApprovals.length === 1
                ? 'Freigeben'
                : `Alle ${message.pendingApprovals.length} freigeben`
              }
            </button>
            <button
              onClick={() => message.pendingApprovals.forEach(a => onReject?.(a.approvalId))}
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Ablehnen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
